import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowLeft, Download, Loader2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface ABTestSession {
  id: string;
  name: string;
  description?: string;
  test_type: string;
  status: string;
  total_votes: number;
}

interface ABTestVariant {
  id: string;
  variant_name: string;
  variant_config: any;
  votes: number;
  image_id?: string;
  is_winner: boolean;
}

const SharedABTest = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<ABTestSession | null>(null);
  const [variants, setVariants] = useState<ABTestVariant[]>([]);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [votingFor, setVotingFor] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [voterEmail, setVoterEmail] = useState('');

  useEffect(() => {
    const fetchSharedTest = async () => {
      if (!token) return;

      try {
        // Fetch session
        const { data: sessionData, error: sessionError } = await supabase
          .from('ab_test_sessions')
          .select('*')
          .eq('share_token', token)
          .eq('is_public', true)
          .single();

        if (sessionError) throw sessionError;
        if (!sessionData) {
          toast.error('Shared test not found or is no longer public');
          navigate('/');
          return;
        }

        setSession(sessionData);

        // Fetch variants
        const { data: variantsData, error: variantsError } = await supabase
          .from('ab_test_variants')
          .select('*')
          .eq('session_id', sessionData.id)
          .order('created_at', { ascending: true });

        if (variantsError) throw variantsError;
        setVariants(variantsData || []);

        // Fetch images
        const imagePromises = variantsData?.map(async (variant) => {
          if (variant.image_id) {
            const { data } = await supabase
              .from('generated_images')
              .select('image_url')
              .eq('id', variant.image_id)
              .single();
            return { variantId: variant.id, url: data?.image_url };
          }
          return { variantId: variant.id, url: null };
        });

        const images = await Promise.all(imagePromises || []);
        const urlMap: Record<string, string> = {};
        images.forEach((img) => {
          if (img.url) urlMap[img.variantId] = img.url;
        });
        setImageUrls(urlMap);
      } catch (error) {
        console.error('Error fetching shared test:', error);
        toast.error('Failed to load shared test');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchSharedTest();
  }, [token, navigate]);

  const handleVote = async (variantId: string) => {
    if (!voterEmail) {
      toast.error('Please enter your email to vote');
      return;
    }

    setVotingFor(variantId);
    try {
      const { error: voteError } = await supabase
        .from('ab_test_votes')
        .insert({
          variant_id: variantId,
          session_id: session?.id,
          voter_type: 'external',
          voter_email: voterEmail,
        });

      if (voteError) throw voteError;

      toast.success('Vote recorded!');
      
      // Refresh variants to show updated vote count
      const { data: updatedVariants } = await supabase
        .from('ab_test_variants')
        .select('*')
        .eq('session_id', session?.id)
        .order('created_at', { ascending: true });

      if (updatedVariants) setVariants(updatedVariants);
    } catch (error) {
      console.error('Vote error:', error);
      toast.error('Failed to record vote');
    } finally {
      setVotingFor(null);
    }
  };

  const handleDownload = (imageUrl: string, variantName: string) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `${variantName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Download started');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!session) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Header />
      <main className="container mx-auto px-4 py-8 pt-24">
        <div className="mb-6">
          <Button variant="secondary" onClick={() => navigate('/')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">{session.name}</h1>
          </div>
          {session.description && (
            <p className="text-gray-300 mb-4">{session.description}</p>
          )}
          <div className="flex gap-3 items-center">
            <Badge variant="secondary">{session.test_type.replace('_', ' ')}</Badge>
            <Badge variant="outline">Total Votes: {session.total_votes}</Badge>
          </div>
        </div>

        <div className="mb-6">
          <Card className="bg-white/5 border-white/10 p-6">
            <label className="text-white font-semibold mb-2 block">
              Your Email (to vote):
            </label>
            <input
              type="email"
              value={voterEmail}
              onChange={(e) => setVoterEmail(e.target.value)}
              placeholder="your@email.com"
              className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400"
            />
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {variants.map((variant) => (
            <Card key={variant.id} className="bg-white/5 border-white/10 p-6">
              <div className="space-y-4">
                <div className="aspect-square bg-white/5 rounded-lg overflow-hidden relative">
                  {imageUrls[variant.id] ? (
                    <img
                      src={imageUrls[variant.id]}
                      alt={variant.variant_name}
                      className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => setSelectedImage(imageUrls[variant.id])}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                    </div>
                  )}
                  {variant.is_winner && (
                    <Badge className="absolute top-2 right-2 bg-yellow-500">
                      Winner 🏆
                    </Badge>
                  )}
                </div>

                <div>
                  <h3 className="text-white font-semibold mb-2">{variant.variant_name}</h3>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {variant.variant_config?.model && (
                      <Badge variant="outline" className="text-xs">
                        {variant.variant_config.model}
                      </Badge>
                    )}
                    {variant.variant_config?.style && (
                      <Badge variant="outline" className="text-xs">
                        {variant.variant_config.style}
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 mb-4">
                    Votes: {variant.votes}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="default"
                    className="flex-1"
                    onClick={() => handleVote(variant.id)}
                    disabled={!voterEmail || votingFor === variant.id}
                  >
                    {votingFor === variant.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Vote'
                    )}
                  </Button>
                  {imageUrls[variant.id] && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(imageUrls[variant.id], variant.variant_name)}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>

      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-slate-900 border-white/10">
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full size"
              className="w-full h-auto rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default SharedABTest;